import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { TPromoBannerBase } from '@onesource/schemas';
import { isRichHtmlEmpty, sanitizeRichHtml, stripHtml } from '@onesource/utils';
import { FC } from 'react';
import { MdChevronRight } from 'react-icons/md';

import { useGetByDevice } from '../../hooks';
import mobileImg from '../banner-with-lauren/mobile-img.png';
import { resolvePromoBannerBackground } from '../promo-banner-colors';
import { promoBannerRichTextSx } from '../promo-banner-rich-text';

export interface SidebarPromoBannerWithLaurenImageMobileProps {
  banner: TPromoBannerBase;
}

// Card padding (theme spacing units) — in the wide single-row layout the image
// cancels it with a negative bottom margin so the pose sits on the card's
// bottom edge.
const CARD_PADDING = 1.75;

/**
 * Mobile / narrow-desktop layout for the sidebar Lauren banner. Below
 * SIDEBAR_BANNER_DESKTOP_MIN_WIDTH pages have no room beside the content
 * column and the slot pins the banner to the bottom of the viewport, so this
 * is a compact floating bar. On phones it follows the Figma mobile frame:
 * headline and the optional review pill fill the left column, the seated
 * Lauren pose sits in the upper-right with the CTA tucked under her. From
 * `sm` up the bar is wide enough for a single row — Lauren anchors the
 * bottom-left edge (bleeding over the padding so she sits on the edge) with
 * the text beside her and the CTA on the right.
 */
export const SidebarPromoBannerWithLaurenImageMobile: FC<SidebarPromoBannerWithLaurenImageMobileProps> = ({
  banner,
}) => {
  const { mobileTitle, mobileDescription, buttonLabel, buttonUrl } = banner;
  const { isDesktop } = useGetByDevice();
  const isExternal = /^https?:\/\//i.test(buttonUrl);

  // Mobile copies are optional — fall back to the desktop content when unset.
  const title = !isDesktop && mobileTitle && !isRichHtmlEmpty(mobileTitle) ? mobileTitle : banner.title;
  const description =
    !isDesktop && mobileDescription && !isRichHtmlEmpty(mobileDescription) ? mobileDescription : banner.description;
  // Admins can hide the review pill without deleting the copy.
  // `!== false` keeps banners visible while an older API omits the flag.
  const isDescriptionVisible = banner.isMobileDescriptionActive !== false;
  // Title is rich HTML; alt must be plain text.
  const imageAlt = stripHtml(title);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateAreas: { xs: '"text img" "text cta"', sm: '"img text cta"' },
        gridTemplateColumns: { xs: '1fr auto', sm: 'auto 1fr auto' },
        gridTemplateRows: { xs: '1fr auto', sm: 'auto' },
        columnGap: { xs: 1, sm: 2.5 },
        rowGap: { xs: 1.25, sm: 1 },
        alignItems: { xs: 'center', sm: 'center' },
        width: '100%',
        p: CARD_PADDING,
        borderRadius: '14px',
        overflow: 'hidden',
        background: resolvePromoBannerBackground(banner.backgroundColor),
        // The bar floats over page content (sticky bottom), so it needs a
        // shadow to separate from whatever scrolls behind it.
        boxShadow: '0 8px 28px rgba(0, 0, 0, 0.25)',
      }}
    >
      <Box
        component="img"
        src={mobileImg}
        alt={imageAlt}
        sx={{
          ml: 2,
          gridArea: 'img',
          alignSelf: 'end',
          // Only the wide single-row layout bleeds Lauren over the card's
          // bottom edge; on phones she stays inside the padding, above the CTA.
          mb: { xs: 0, sm: -CARD_PADDING },
          width: { xs: 51, sm: 88 },
          maxHeight: isDescriptionVisible ? 118 : { xs: 83, sm: 88 },
          objectFit: 'contain',
          objectPosition: 'bottom',
        }}
      />

      <Stack spacing={{ xs: 1.25, sm: 0.75 }} sx={{ gridArea: 'text', minWidth: 0, py: 0.5 }}>
        <Box
          sx={{
            ...promoBannerRichTextSx,
            color: theme => theme.palette.common.white,
            fontWeight: 700,
            fontSize: 13,
            lineHeight: '19px',
            mb: 5.5,
            // Redefining `& span` clobbers the base rule from
            // promoBannerRichTextSx, so re-apply the margin reset here.
            '& span': { fontSize: 13, margin: isDesktop ? '2px 0 !important' : undefined },
          }}
          dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(title) }}
        />

        {isDescriptionVisible && !isRichHtmlEmpty(description) && (
          <Box
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              borderRadius: '8px',
              px: 1.25,
              py: 0.75,
              alignSelf: 'flex-start',
            }}
          >
            <Box
              sx={{
                ...promoBannerRichTextSx,
                fontStyle: 'italic',
                color: '#000',
                fontSize: '9px',
                lineHeight: 1.35,
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(description) }}
            />
          </Box>
        )}
      </Stack>

      <Button
        variant="outlined"
        href={buttonUrl}
        {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        endIcon={<MdChevronRight size={16} />}
        sx={{
          gridArea: 'cta',
          alignSelf: { xs: 'end', sm: 'center' },
          justifySelf: 'end',
          width: 60,
          minHeight: '26px',
          maxHeight: '26px',
          borderRadius: '8px',
          color: '#fff !important',
          borderColor: theme => theme.palette.common.white,
          fontSize: '9px',
          fontWeight: 700,
          px: 1.2,

          '& .MuiButton-endIcon': { ml: -0.9, mr: -1.5 },
          '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255, 255, 255, 0.14)' },
        }}
      >
        {buttonLabel}
      </Button>
    </Box>
  );
};
